import Phaser from 'phaser';
import { DANGER_LINE_Y } from '../../config/gameplay';
import type { PhysicsStepObservation } from '../../game/simulation/types';
import { readMetadata, type BodyFactory } from './BodyFactory';

type MatterCollisionEvent = Readonly<{
  pairs: readonly Phaser.Types.Physics.Matter.MatterCollisionPair[];
}>;

export class CollisionCoordinator {
  private step = 0;
  private readonly contacts = new Map<string, PhysicsStepObservation['contacts'][number]>();
  private readonly firstContacts = new Set<string>();
  private readonly contacted = new Map<string, number>();

  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly bodyFactory: BodyFactory,
    private readonly onStep: (observation: PhysicsStepObservation) => void,
    private readonly onImpact: (impact: Readonly<{ relativeSpeed: number; combinedMass: number }>) => void,
    private readonly beforeStep: () => void = () => undefined,
  ) {
    scene.matter.world.on('beforeupdate', this.beforeUpdate, this);
    scene.matter.world.on('collisionstart', this.collisionStart, this);
    scene.matter.world.on('collisionactive', this.collisionActive, this);
    scene.matter.world.on('afterupdate', this.afterUpdate, this);
  }

  public destroy(): void {
    this.scene.matter.world.off('beforeupdate', this.beforeUpdate, this);
    this.scene.matter.world.off('collisionstart', this.collisionStart, this);
    this.scene.matter.world.off('collisionactive', this.collisionActive, this);
    this.scene.matter.world.off('afterupdate', this.afterUpdate, this);
  }

  private beforeUpdate(): void {
    this.step += 1;
    this.contacts.clear();
    this.firstContacts.clear();
    this.beforeStep();
    this.bodyFactory.clampSpeeds();
  }

  private collisionStart(event: MatterCollisionEvent): void {
    this.collect(event, true);
  }

  private collisionActive(event: MatterCollisionEvent): void {
    this.collect(event, false);
  }

  private collect(event: MatterCollisionEvent, markFirst: boolean): void {
    for (const pair of event.pairs) {
      const left = readMetadata(pair.bodyA);
      const right = readMetadata(pair.bodyB);
      if (markFirst) {
        if (left && this.contacted.get(left.entityId) !== left.revision) {
          this.contacted.set(left.entityId, left.revision);
          this.firstContacts.add(left.entityId);
        }
        if (right && this.contacted.get(right.entityId) !== right.revision) {
          this.contacted.set(right.entityId, right.revision);
          this.firstContacts.add(right.entityId);
        }
        if (left || right) {
          const relativeSpeed = Math.hypot(
            pair.bodyA.velocity.x - pair.bodyB.velocity.x,
            pair.bodyA.velocity.y - pair.bodyB.velocity.y,
          );
          this.onImpact({
            relativeSpeed,
            combinedMass:
              (pair.bodyA.isStatic ? 0 : Math.max(0, pair.bodyA.mass)) +
              (pair.bodyB.isStatic ? 0 : Math.max(0, pair.bodyB.mass)),
          });
        }
      }
      if (!left || !right || left.entityId === right.entityId || left.generation !== right.generation) continue;
      const [a, b] = left.entityId < right.entityId
        ? [left.entityId, right.entityId]
        : [right.entityId, left.entityId];
      const point = bodyCenterMidpoint(pair.bodyA, pair.bodyB);
      const key = `${a}\u0000${b}`;
      if (!this.contacts.has(key)) this.contacts.set(key, { a, b, point });
    }
  }

  private afterUpdate(): void {
    this.onStep({
      step: this.step,
      nowMs: this.scene.time.now,
      contacts: [...this.contacts.values()],
      firstContacts: [...this.firstContacts],
      danger: [],
      escapedEntityIds: this.bodyFactory.escapedEntityIds(),
    });
  }
}

export function bodyCenterMidpoint(
  left: Pick<MatterJS.BodyType, 'position' | 'parent'>,
  right: Pick<MatterJS.BodyType, 'position' | 'parent'>,
): Readonly<{ x: number; y: number }> {
  const leftRoot = left.parent && left.parent !== left ? left.parent : left;
  const rightRoot = right.parent && right.parent !== right ? right.parent : right;
  return {
    x: (leftRoot.position.x + rightRoot.position.x) / 2,
    y: (leftRoot.position.y + rightRoot.position.y) / 2,
  };
}
